/**
 * Admin Backups Module
 * Database backup management with Google Cloud Storage
 */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { adminApiGet, adminApiPatch, adminApiPost } from '../../utils/adminApi';
import toast from '../../utils/toast';

import {
  Calendar,
  CheckCircle,
  CircleNotch,
  Clock,
  Cloud,
  Database,
  DownloadSimple,
  GearSix as SettingsIcon,
  HardDrive,
  Play,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
const AdminBackups = () => {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [filters, setFilters] = useState({
    range: '30d',
    status: 'all'
  });

  useEffect(() => {
    fetchSettings();
    fetchHistory();
  }, [filters]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await adminApiGet('/api/admin/backups/settings');
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load backup settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const data = await adminApiGet('/api/admin/backups/history', filters);
      setHistory(data.history || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings || !settings.settings) return;
    
    try {
      setSaving(true);
      await adminApiPatch('/api/admin/backups/settings', settings.settings);
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRunBackup = async () => {
    if (!window.confirm('Run a manual backup now?')) return;
    
    try {
      setRunning(true);
      const data = await adminApiPost('/api/admin/backups/run');
      toast.success('Backup completed successfully');
      if (data.note) {
        toast.info('Note', data.note);
      }
      // Refresh both settings (for Last Backup card) and history
      fetchSettings();
      fetchHistory();
    } catch (error) {
      console.error('Error running backup:', error);
      toast.error(error.message || 'Backup failed');
    } finally {
      setRunning(false);
    }
  };

  const updateSetting = (key, value) => {
    if (!settings || !settings.settings) return;
    
    setSettings({
      ...settings,
      settings: {
        ...settings.settings,
        [key]: value
      }
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-12 bg-white/10 rounded animate-pulse w-64"></div>
        <div className="grid grid-cols-2 gap-6">
          {[1, 2].map(i => (
            <div key={i} className="h-96 bg-white/10 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Database Backups</h1>
          <p className="text-gray-400">Configure and manage automated database backups</p>
        </div>
      </div>

      {/* GCS Not Configured Banner */}
      {settings && !settings.gcs_configured && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
          <WarningCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-red-500 font-semibold mb-1">Google Cloud Storage not configured</h3>
            <p className="text-sm text-red-200/80">
              Backups are disabled. Add GCS_BACKUP_BUCKET and GCP_CREDENTIALS to .env to enable backup functionality.
            </p>
          </div>
        </div>
      )}

      {/* GearSix Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* GearSix Card */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
            <SettingsIcon size={20} />
            Backup GearSix
          </h3>

          <div className="space-y-4">
            {/* Enable Toggle */}
            <div className="flex items-center justify-between pb-4 border-b border-white/10">
              <div>
                <label className="text-white font-medium">Scheduled Backups</label>
                <p className="text-xs text-gray-400">Automatically backup database</p>
              </div>
              <button
                onClick={() => updateSetting('enabled', !settings?.settings?.enabled)}
                disabled={!settings?.gcs_configured}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings?.settings?.enabled ? 'bg-[#6366F1]' : 'bg-white/20'
                } ${!settings?.gcs_configured ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings?.settings?.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Frequency */}
            <div>
              <label className="text-white font-medium text-sm mb-2 block">Frequency</label>
              <select
                value={settings?.settings?.frequency || 'daily'}
                onChange={(e) => updateSetting('frequency', e.target.value)}
                disabled={!settings?.settings?.enabled}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50 disabled:opacity-50"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white font-medium text-sm mb-2 block">Hour (UTC)</label>
                <input
                  type="number"
                  min="0"
                  max="23"
                  value={settings?.settings?.time_hour || 2}
                  onChange={(e) => updateSetting('time_hour', parseInt(e.target.value))}
                  disabled={!settings?.settings?.enabled}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50 disabled:opacity-50"
                />
              </div>
              <div>
                <label className="text-white font-medium text-sm mb-2 block">Minute</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={settings?.settings?.time_minute || 0}
                  onChange={(e) => updateSetting('time_minute', parseInt(e.target.value))}
                  disabled={!settings?.settings?.enabled}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50 disabled:opacity-50"
                />
              </div>
            </div>

            {/* Retention */}
            <div>
              <label className="text-white font-medium text-sm mb-2 block">Keep Last N Backups</label>
              <input
                type="number"
                min="1"
                max="90"
                value={settings?.settings?.retention_count || 7}
                onChange={(e) => updateSetting('retention_count', parseInt(e.target.value))}
                disabled={!settings?.settings?.enabled}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50 disabled:opacity-50"
              />
              <p className="text-xs text-gray-400 mt-1">Older backups will be automatically deleted</p>
            </div>

            {/* GCS Bucket (Read-only) */}
            {settings && settings.gcs_configured && (
              <div>
                <label className="text-white font-medium text-sm mb-2 block">GCS Bucket</label>
                <input
                  type="text"
                  value={settings?.gcs_bucket || ''}
                  readOnly
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-gray-400"
                />
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSaveSettings}
              disabled={saving || !settings?.gcs_configured}
              className="w-full px-4 py-2 bg-[#6366F1] hover:bg-[#5558E3] rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <CircleNotch size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </button>
          </div>
        </div>

        {/* Status Card */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
            <Database size={20} />
            Backup Status
          </h3>

          <div className="space-y-4">
            {/* Next Scheduled */}
            <div className="bg-white/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={16} className="text-gray-400" />
                <span className="text-sm font-medium text-gray-400">Next Scheduled Backup</span>
              </div>
              <p className="text-white font-semibold">
                {settings?.settings?.enabled ? 'Daily at 02:00 UTC' : 'Disabled'}
              </p>
            </div>

            {/* Last Backup */}
            {settings && settings.last_backup && (
              <div className="bg-white/5 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={16} className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-400">Last Backup</span>
                </div>
                <div className="flex items-center gap-2">
                  {settings.last_backup.status === 'success' ? (
                    <CheckCircle size={16} className="text-green-500" />
                  ) : (
                    <XCircle size={16} className="text-red-500" />
                  )}
                  <p className="text-white font-semibold">
                    {new Date(settings.last_backup.timestamp).toLocaleString()}
                  </p>
                </div>
                {settings.last_backup.size_mb && (
                  <p className="text-xs text-gray-400 mt-1">
                    Size: {settings.last_backup.size_mb} MB
                  </p>
                )}
              </div>
            )}

            {/* Manual Backup Button */}
            <button
              onClick={handleRunBackup}
              disabled={running || !settings?.gcs_configured}
              className="w-full px-4 py-3 bg-gradient-to-r from-[#6366F1] to-[#EC4899] hover:opacity-90 rounded-lg transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-semibold"
            >
              {running ? (
                <>
                  <CircleNotch size={18} className="animate-spin" />
                  Running Backup...
                </>
              ) : (
                <>
                  <Play size={18} />
                  Run Manual Backup Now
                </>
              )}
            </button>

            {settings && settings.gcs_configured && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-start gap-2">
                <Cloud size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-200/80">
                  Backups are stored securely in Google Cloud Storage and encrypted at rest.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Backup History */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-semibold text-lg flex items-center gap-2">
              <HardDrive size={20} />
              Backup History
            </h3>

            {/* Filters */}
            <div className="flex gap-2">
              <select
                value={filters.range}
                onChange={(e) => setFilters({...filters, range: e.target.value})}
                className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>

              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-[#6366F1]/50"
              >
                <option value="all">All Status</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </div>

        <table className="w-full">
          <thead className="bg-white/5 border-b border-white/10">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Date/Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Size</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {history.length > 0 ? (
              history.map((backup) => (
                <tr key={backup.backup_id} className="hover:bg-white/5">
                  <td className="px-6 py-4 text-sm text-white">
                    {new Date(backup.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      backup.type === 'manual'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {backup.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex items-center gap-2">
                      {backup.status === 'success' ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : (
                        <XCircle size={16} className="text-red-500" />
                      )}
                      <span className="text-white capitalize">{backup.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-white text-right">
                    {backup.size_mb ? `${backup.size_mb} MB` : '–'}
                  </td>
                  <td className="px-6 py-4 text-sm text-white text-right">
                    <button
                      disabled
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download not yet implemented"
                    >
                      <DownloadSimple size={16} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center">
                  <Database size={48} className="text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400">No backups found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminBackups;
